import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  Speed,
  Security,
  ArrowForward,
} from '@mui/icons-material';

const CaseStudiesPage: React.FC = () => {
  const caseStudies = [
    {
      title: 'Federal Healthcare Agency Digital Transformation',
      client: 'U.S. Department of Health',
      challenge: 'Legacy systems unable to handle increased demand for digital services',
      solution: 'Cloud-native architecture with microservices and API-first design',
      results: [
        '75% reduction in system downtime',
        '60% improvement in processing speed',
        '$2.5M annual cost savings',
        '99.99% uptime achieved',
      ],
      technologies: ['AWS', 'Kubernetes', 'React', 'Node.js'],
      category: 'Custom Software',
    },
    {
      title: 'State Government IT Infrastructure Modernization',
      client: 'State of Virginia',
      challenge: 'Outdated infrastructure causing frequent outages and security vulnerabilities',
      solution: 'Comprehensive managed IT services with 24/7 monitoring and support',
      results: [
        '90% reduction in security incidents',
        '24/7 uptime guarantee met',
        '40% reduction in IT costs',
        'NIST compliance achieved',
      ],
      technologies: ['Azure', 'VMware', 'Cisco', 'Splunk'],
      category: 'Managed IT',
    },
    {
      title: 'Defense Contractor Supply Chain Optimization',
      client: 'Major Defense Contractor',
      challenge: 'Inefficient supply chain causing delays and increased costs',
      solution: 'AI-powered logistics optimization platform with real-time tracking',
      results: [
        '35% reduction in delivery times',
        '25% decrease in logistics costs',
        '50% improvement in inventory accuracy',
        'ROI achieved in 6 months',
      ],
      technologies: ['Machine Learning', 'IoT', 'Python', 'PostgreSQL'],
      category: 'Logistics',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8f 0%, #3b82f6 100%)',
          color: 'white',
          pt: 12,
          pb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Case Studies
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.95 }}>
            Real-world success stories demonstrating our impact on government and enterprise clients.
          </Typography>
        </Container>
      </Box>

      {/* Case Studies */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          {caseStudies.map((study, index) => (
            <Card key={index} sx={{ mb: 4 }}>
              <Grid container>
                <Grid item xs={12} md={8}>
                  <CardContent sx={{ p: 4 }}>
                    <Chip label={study.category} color="primary" sx={{ mb: 2 }} />
                    <Typography variant="h4" gutterBottom>
                      {study.title}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Client: {study.client}
                    </Typography>
                    
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Challenge
                      </Typography>
                      <Typography variant="body1" paragraph>
                        {study.challenge}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Solution
                      </Typography>
                      <Typography variant="body1" paragraph>
                        {study.solution}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Technologies Used
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {study.technologies.map((tech) => (
                          <Chip key={tech} label={tech} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>

                    <Button
                      variant="contained"
                      endIcon={<ArrowForward />}
                      sx={{ mt: 3 }}
                    >
                      Download Full Case Study
                    </Button>
                  </CardContent>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h5" gutterBottom>
                      Key Results
                    </Typography>
                    {study.results.map((result, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TrendingUp sx={{ mr: 2 }} />
                        <Typography variant="body1">
                          {result}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Card>
          ))}
        </Container>
      </Box>
    </Box>
  );
};

export default CaseStudiesPage;